import React, { useEffect, useState } from "react";
import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Typography } from "@material-ui/core";
import { useCookies } from "react-cookie";
import { Spinner } from "../../components";
import { White } from "../../shared/styles/colors";
import Pathnames from "../../model/enum/Pathnames";
import DatabaseEntities from "../../model/enum/DatabaseEntities";
import ApiService from "../../service/ApiService";
import SmallLogo from "../../assets/icon.png";
import Logo from "../../assets/main.png";
import DashboardIcon from "../../assets/admin/sidebar/dashboard.svg";
import MediaIcon from "../../assets/admin/sidebar/media.svg";
import ContentIcon from "../../assets/admin/sidebar/content.svg";
import GeneralIcon from "../../assets/admin/sidebar/general.svg";
import InternalIcon from "../../assets/admin/sidebar/internal.svg";
import SettingIcon from "../../assets/admin/sidebar/setting.svg";
import LogoutIcon from "../../assets/admin/sidebar/logout.svg";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import useStyles from "./styles";
import Permission from "../../model/permission";
import PageResultResponse from "../../model/pageResultResponse";
import Misc from "../../utils/Misc";
import RolePermission from "../../model/rolePermission";

const Side = () => {
  const classes = useStyles();
  const [cookies, setCookie, removeCookie] = useCookies(["session"]);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  let pathname = location.pathname;

  const checkLocation = (loc: any) => {
    return pathname === loc;
  };

  const handleCollapse = () => setOpen((open) => !open);

  const logout = async () => {
    setLoading(true);
    removeCookie("session");
    localStorage.clear();
    await ApiService.post<any>(`${DatabaseEntities.AUTH}/logout`, {});
    setLoading(false);
    window.open(Pathnames.LOGIN, "_self");
  };

  var accessToken = localStorage.getItem("accessToken");

  const allowedPaths = (): boolean => {
    const adminBase = Pathnames.ADMIN.replace(/\/$/, ""); // remove trailing slash if any
    return (
      pathname !== Pathnames.LOGIN &&
      pathname !== Pathnames.ACTIVATE_ACCOUNT &&
      pathname.startsWith(adminBase)
    );
  };

  const isAuthenticated = () => {
    return (
      !Misc.isNullOrEmpty(cookies.session) && !Misc.isNullOrEmpty(accessToken)
    );
  };

  //Check if user is logged in
  useEffect(() => {
    document.title = "Admin - Setup Remodeling";

    if (!isAuthenticated() && allowedPaths()) {
      localStorage.clear();
      setLoading(false);
      window.open(Pathnames.LOGIN, "_self");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (window.innerWidth <= 640) {
      setOpen(true);
      handleCollapse();
    }
  });

  useEffect(() => {
    const fecthData = async () => {
      var userRoleId = localStorage.getItem("userRoleId");

      if (Misc.isNullOrEmpty(userRoleId)) {
        return;
      }

      setLoading(true);
      await ApiService.get<Permission[]>(
        `${DatabaseEntities.ROLES}/${userRoleId}/${DatabaseEntities.PERMISSIONS}`,
      )
        .then((data: Permission[]) => {
          if (Misc.isArrayEmpty(data)) return;
          localStorage.setItem("permissions", JSON.stringify(data ?? []));
        })
        .finally(() => {
          setLoading(false);
        });
    };

    //if (isAuthenticated() && allowedPaths()) fecthData();

    // Permissions are stored in localStorage, so we can check if the user has access to the current path and redirect if not
    // Change logic to request if user has permissions

  }, [location.pathname]); // Add location.pathname as a dependency to avoid infinite loop

  const allowedAccess = (permissionName: string, tab?: string): boolean => {
    const userPermissions = localStorage.getItem("permissions");

    if (Misc.isNullOrEmpty(userPermissions)) {
      return false;
    }

    if (tab) {
      switch (tab) {
        case "media":
          return allowedAccess("View Gallery") || allowedAccess("View Reviews");
        case "content":
          return (
            allowedAccess("View Appointments") ||
            allowedAccess("View Requests") ||
            allowedAccess("View Contacts") ||
            allowedAccess("View Clients") ||
            allowedAccess("View Products & Services") ||
            allowedAccess("View Estimates") ||
            allowedAccess("View Teams & Employees") ||
            allowedAccess("View Projects")
          );
        case "general":
          return (
            allowedAccess("View Services") ||
            allowedAccess("View FAQs") ||
            allowedAccess("View Tags")
          );
        case "internal":
          return (
            allowedAccess("View Users") ||
            allowedAccess("View Roles") ||
            allowedAccess("View Permissions") ||
            allowedAccess("View Logs") ||
            allowedAccess("View Errors")
          );
        default:
          return false;
      }
    }

    const parsedPermissions: Permission[] = JSON.parse(userPermissions!);
    return parsedPermissions.some(
      (permission: Permission) => permission.name === permissionName,
    );
  };

  return (
    <>
      {loading && <Spinner />}
      <Sidebar
        id="sidebar-div"
        className={classes.sidebar}
        collapsed={!open}
        backgroundColor={White}
      >
        {open ? (
          <div className="d-flex row">
            <div className={classes.topDiv}>
              <Button onClick={handleCollapse} className={classes.topButton}>
                <CloseIcon />
                {/**Change the condition */}
              </Button>
            </div>
            <img src={Logo} className={classes.fullLogo} alt="logo" />
          </div>
        ) : (
          <div className="d-flex row align-items-center justify-content-center">
            {window.innerWidth > 640 && (
              <Button onClick={handleCollapse}>
                <MenuIcon />
              </Button>
            )}
            <img
              src={SmallLogo}
              className={classes.logo}
              alt="icon"
              onClick={() => navigate(Pathnames.ADMIN)}
            />
          </div>
        )}
        <Menu className={classes.menu} closeOnClick>
          <MenuItem
            hidden={!allowedAccess("DASHBOARD")}
            component={
              <Link
                to={Pathnames.DASHBOARD}
                className={
                  checkLocation(Pathnames.DASHBOARD) ? classes.selectedMenu : ""
                }
              />
            }
          >
            <Typography
              className={
                checkLocation(Pathnames.DASHBOARD)
                  ? classes.selectedText
                  : classes.text
              }
            >
              <img
                src={DashboardIcon}
                className={classes.icon}
                alt="dashboard"
              />
              {open && <span>Dashboard</span>}
            </Typography>
          </MenuItem>
          <SubMenu
            hidden={
              !allowedAccess(
                "",
                "media",
              ) /* Check if user has access to any media tab */
            }
            label={
              <>
                <img src={MediaIcon} className={classes.icon} alt="media" />
                {open && <span>Media Management</span>}
              </>
            }
            className={classes.text}
          >
            <MenuItem
              hidden={!allowedAccess("View Gallery")}
              component={<Link to={Pathnames.GALLERY_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.GALLERY_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Gallery
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Reviews")}
              component={<Link to={Pathnames.REVIEWS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.REVIEWS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Reviews
              </Typography>
            </MenuItem>
          </SubMenu>
          <SubMenu
            hidden={
              !allowedAccess(
                "",
                "content",
              ) /* Check if user has access to any content tab */
            }
            label={
              <>
                <img src={ContentIcon} className={classes.icon} alt="content" />
                {open && <span>Content Management</span>}
              </>
            }
            className={classes.text}
          >
            <MenuItem
              hidden={!allowedAccess("View Appointments")}
              component={<Link to={Pathnames.APPOINTMENTS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.APPOINTMENTS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Appointments
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Requests")}
              component={<Link to={Pathnames.REQUESTS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.REQUESTS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Requests
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Messages")}
              component={<Link to={Pathnames.CONTACTS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.CONTACTS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Messages
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Clients")}
              component={<Link to={Pathnames.CLIENTS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.CLIENTS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Clients
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Products")}
              component={<Link to={Pathnames.PRODUCTS_SERVICES_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.PRODUCTS_SERVICES_LIST) ||
                  checkLocation(Pathnames.PRODUCTS_SERVICES_BUNDLE_LIST) ||
                  checkLocation(Pathnames.PRODUCTS_SERVICES_CATEGORY_LIST) ||
                  checkLocation(Pathnames.PRODUCTS_SERVICES_VENDOR_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Products & Services
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Estimates")}
              component={<Link to={Pathnames.ESTIMATES_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.ESTIMATES_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Estimates
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Employees")}
              component={<Link to={Pathnames.TEAM_EMPLOYEE_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.TEAM_EMPLOYEE_LIST) ||
                  checkLocation(Pathnames.TEAMS_LIST) ||
                  checkLocation(Pathnames.EMPLOYEES_LIST) ||
                  checkLocation(Pathnames.CONTRACTORS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Teams & Employees
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Projects")}
              component={<Link to={Pathnames.PROJECTS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.PROJECTS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Projects
              </Typography>
            </MenuItem>
          </SubMenu>
          <SubMenu
            hidden={
              !allowedAccess(
                "",
                "general",
              ) /* Check if user has access to any general tab */
            }
            label={
              <>
                <img src={GeneralIcon} className={classes.icon} alt="general" />
                {open && <span>General Management</span>}
              </>
            }
            className={classes.text}
          >
            <MenuItem
              //hidden={!allowedAccess("View Request Services")}
              component={<Link to={Pathnames.SERVICES_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.SERVICES_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Request Services
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View FAQs")}
              component={<Link to={Pathnames.FAQ_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.FAQ_LIST) ? classes.selectedMenu : ""
                }
              >
                FAQs
              </Typography>
            </MenuItem>
            <MenuItem
              //hidden={!allowedAccess("View Tags")}
              component={<Link to={Pathnames.TAGS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.TAGS_LIST) ||
                  checkLocation(Pathnames.TAG_GROUPS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Tags
              </Typography>
            </MenuItem>
            {/* <MenuItem component={<Link to={Pathnames.STATUS_LIST} />}>
              <Typography
                className={
                  checkLocation(Pathnames.STATUS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Pending Requests
              </Typography>
            </MenuItem> */}
          </SubMenu>
          <SubMenu
            hidden={
              !allowedAccess(
                "",
                "internal",
              ) /* Check if user has access to any internal tab */
            }
            label={
              <>
                <img
                  src={InternalIcon}
                  className={classes.icon}
                  alt="internal"
                />
                {open && <span>Internal Services</span>}
              </>
            }
            className={classes.text}
          >
            <MenuItem
              hidden={!allowedAccess("View Users")}
              component={<Link to={Pathnames.USERS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.USERS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Users
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Roles")}
              component={<Link to={Pathnames.ROLES_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.ROLES_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Roles
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Permissions")}
              component={<Link to={Pathnames.PERMISSIONS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.PERMISSIONS_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Permissions
              </Typography>
            </MenuItem>
            {/* <MenuItem component={<Link to={Pathnames.EMAIL_LIST} />}>
              <Typography
                className={
                  checkLocation(Pathnames.EMAIL_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Emails
              </Typography>
            </MenuItem> */}
            {/* <MenuItem component={<Link to={Pathnames.MESSAGE_LIST} />}>
              <Typography
                className={
                  checkLocation(Pathnames.MESSAGE_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Messages
              </Typography>
            </MenuItem> */}
            <MenuItem
              hidden={!allowedAccess("View Logs")}
              component={<Link to={Pathnames.LOGS_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.LOGS_LIST) ? classes.selectedMenu : ""
                }
              >
                Logs
              </Typography>
            </MenuItem>
            <MenuItem
              hidden={!allowedAccess("View Errors")}
              component={<Link to={Pathnames.ERROR_LIST} />}
            >
              <Typography
                className={
                  checkLocation(Pathnames.ERROR_LIST)
                    ? classes.selectedMenu
                    : ""
                }
              >
                Errors
              </Typography>
            </MenuItem>
          </SubMenu>
          <MenuItem
            component={
              <Link
                to={Pathnames.SETTINGS}
                className={
                  checkLocation(Pathnames.SETTINGS) ? classes.selectedMenu : ""
                }
              />
            }
          >
            <Typography
              className={
                checkLocation(Pathnames.SETTINGS)
                  ? classes.selectedText
                  : classes.text
              }
            >
              <img src={SettingIcon} className={classes.icon} alt="setting" />
              {open && <span>Settings</span>}
            </Typography>
          </MenuItem>
          <MenuItem onClick={logout}>
            <Typography className={classes.text}>
              <img src={LogoutIcon} className={classes.icon} alt="logout" />
              {open && <span>Logout</span>}
            </Typography>
          </MenuItem>
        </Menu>
        <div className={classes.versionDiv}>
          <Typography className={classes.versionText}>V1.4.2</Typography>
        </div>
      </Sidebar>
    </>
  );
};

export default Side;
